import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Check, Clock, Heart, MessageSquare, RefreshCw, ShieldCheck, Download, FileCode, History, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useApi, errMsg, money, imgUrl, fmtDate, downloadBlob } from "@/lib/api";
import { DemoBadge, EmptyState, Spinner, Stars, Tag, Verified } from "@/components/shared/common";
import { ServiceCard } from "@/components/shared/Cards";

export async function startCheckout(api, orderId, milestoneId = "") {
  const { data } = await api.post("/payments/checkout", { order_id: orderId, milestone_id: milestoneId, origin_url: window.location.origin });
  window.location.href = data.checkout_url;
}

export function useFavorite(kind, id, initial) {
  const api = useApi();
  const { isSignedIn } = useAuth();
  const nav = useNavigate();
  const [fav, setFav] = useState(initial);
  useEffect(() => { setFav(initial); }, [initial]);
  const toggle = async () => {
    if (!isSignedIn) return nav("/sign-in");
    try { const { data } = await api.post(`/favorites/${kind}/${id}`); setFav(data.favorite); toast.success(data.favorite ? "Saved to favorites" : "Removed from favorites"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return [fav, toggle];
}

export function ContactSeller({ vendor, testId = "contact-seller-button" }) {
  const api = useApi();
  const nav = useNavigate();
  const { isSignedIn } = useAuth();
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const send = async () => {
    try { const { data } = await api.post("/conversations", { vendor_id: vendor.id, body }); setOpen(false); nav(`/messages/${data.conversation_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (isSignedIn ? setOpen(o) : nav("/sign-in"))}>
      <DialogTrigger asChild><Button variant="outline" className="w-full" data-testid={testId}><MessageSquare className="mr-2 h-4 w-4" />Contact {vendor.type === "agency" ? "agency" : "seller"}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Message {vendor.display_name}</DialogTitle></DialogHeader>
        {vendor.is_demo && <p className="text-sm text-amber-600">This is a demo storefront — messages can't be delivered.</p>}
        <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Describe what you need…" data-testid="contact-message-input" />
        <Button onClick={send} disabled={!body.trim()} data-testid="contact-send-button">Send message</Button>
      </DialogContent>
    </Dialog>
  );
}

const SellerBox = ({ vendor }) => (
  <div className="card-surface p-6">
    <Link to={`/store/${vendor.slug}`} className="flex items-center gap-3" data-testid="seller-store-link">
      {vendor.avatar_url && <img src={imgUrl(vendor.avatar_url)} alt="" className="h-12 w-12 rounded-xl object-cover" />}
      <div><div className="flex items-center gap-1.5 font-semibold">{vendor.display_name}{vendor.verified && <Verified />}</div><p className="text-sm text-muted-foreground">{vendor.headline}</p></div>
    </Link>
    <div className="mt-4 flex items-center justify-between text-sm"><Stars avg={vendor.rating_avg} count={vendor.rating_count} /><span className="text-muted-foreground">{vendor.location}</span></div>
    <div className="mt-4"><ContactSeller vendor={vendor} /></div>
  </div>
);

export const Reviews = ({ reviews }) => (
  <div className="space-y-4" data-testid="reviews-list">
    {reviews.length === 0 ? <p className="text-sm text-muted-foreground">No reviews yet. Reviews can only be left by buyers after a completed order.</p> : reviews.map((r) => (
      <div key={r.id} className="border-b border-border pb-4">
        <div className="flex items-center justify-between text-sm"><span className="font-semibold">{r.buyer_name}</span><Stars avg={r.rating} count={1} /></div>
        <p className="mt-1 text-xs text-emerald-600">Verified purchase · {fmtDate(r.created_at)}</p>
        <p className="mt-2 text-sm">{r.comment}</p>
        {r.vendor_reply && <p className="mt-2 border-l-2 border-primary pl-3 text-sm text-muted-foreground">Seller reply: {r.vendor_reply}</p>}
      </div>
    ))}
  </div>
);

const Gallery = ({ images, title }) => {
  const [i, setI] = useState(0);
  if (!images?.length) return <div className="grid-bg aspect-[16/9] rounded-xl border border-border" />;
  return (
    <div>
      <div className="aspect-[16/9] overflow-hidden rounded-xl border border-border bg-muted"><img src={imgUrl(images[i])} alt={title} className="h-full w-full object-cover" /></div>
      {images.length > 1 && <div className="mt-3 flex gap-2">{images.map((src, j) => (
        <button key={j} onClick={() => setI(j)} className={`h-14 w-20 overflow-hidden rounded-lg border-2 ${i === j ? "border-primary" : "border-transparent"}`} data-testid={`gallery-thumb-${j}`}><img src={imgUrl(src)} alt="" className="h-full w-full object-cover" /></button>
      ))}</div>}
    </div>
  );
};

function PackagePanel({ s, commission }) {
  const api = useApi();
  const nav = useNavigate();
  const { isSignedIn } = useAuth();
  const [tier, setTier] = useState(s.packages.find((p) => p.tier === "standard")?.tier || s.packages[0].tier);
  const [addons, setAddons] = useState([]);
  const [req, setReq] = useState("");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [busy, setBusy] = useState(false);
  const pkg = s.packages.find((p) => p.tier === tier);
  const subtotal = pkg.price_cents + s.addons.filter((a) => addons.includes(a.name)).reduce((t, a) => t + a.price_cents, 0);
  const checkCoupon = async () => {
    try { const { data } = await api.get("/coupons/validate", { params: { code: coupon, vendor_id: s.vendor_id, subtotal_cents: subtotal } }); setDiscount(data.discount_cents); toast.success(`${data.percent_off}% off applied`); }
    catch (e) { setDiscount(0); toast.error(errMsg(e)); }
  };
  const order = async () => {
    if (!isSignedIn) return nav("/sign-in");
    setBusy(true);
    try { const { data } = await api.post("/orders/service", { service_id: s.id, tier, addons, requirements: req, coupon_code: discount ? coupon : "" }); await startCheckout(api, data.order_id); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="card-surface overflow-hidden" data-testid="package-panel">
      <div className="grid grid-cols-3 border-b border-border">
        {s.packages.map((p) => (
          <button key={p.tier} onClick={() => setTier(p.tier)} data-testid={`package-select-${p.tier}-button`}
            className={`px-3 py-3 text-sm font-semibold capitalize transition-colors ${tier === p.tier ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}>{p.tier}</button>
        ))}
      </div>
      <div className="space-y-4 p-6">
        <div className="flex items-baseline justify-between"><h3 className="font-semibold">{pkg.name}</h3><span className="text-2xl font-bold" data-testid="package-price">{money(pkg.price_cents)}</span></div>
        <p className="text-sm text-muted-foreground">{pkg.description}</p>
        <div className="flex gap-4 text-sm"><span className="flex items-center gap-1"><Clock className="h-4 w-4" />{pkg.delivery_days} days</span><span className="flex items-center gap-1"><RefreshCw className="h-4 w-4" />{pkg.revisions} revisions</span></div>
        <ul className="space-y-1.5">{pkg.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-500" />{f}</li>)}</ul>
        {s.addons.length > 0 && <div className="space-y-2 border-t border-border pt-4"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add-ons</p>
          {s.addons.map((a, i) => (
            <label key={a.name} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2"><Checkbox checked={addons.includes(a.name)} onCheckedChange={(c) => setAddons(c ? [...addons, a.name] : addons.filter((x) => x !== a.name))} data-testid={`addon-checkbox-${i}`} />{a.name}</span>
              <span>+{money(a.price_cents)}</span>
            </label>
          ))}</div>}
        <Textarea rows={3} placeholder={s.requirements || "Tell the seller what you need"} value={req} onChange={(e) => setReq(e.target.value)} data-testid="order-requirements-input" />
        <div className="flex gap-2"><Input placeholder="Coupon code" value={coupon} onChange={(e) => { setCoupon(e.target.value); setDiscount(0); }} data-testid="coupon-input" /><Button variant="outline" onClick={checkCoupon} disabled={!coupon} data-testid="coupon-apply-button">Apply</Button></div>
        <div className="space-y-1 border-t border-border pt-4 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(subtotal)}</span></div>
          {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>−{money(discount)}</span></div>}
          <div className="flex justify-between text-base font-bold"><span>Total</span><span data-testid="order-total">{money(subtotal - discount)}</span></div>
          <p className="text-xs text-muted-foreground">No buyer fees. The seller pays a {commission}% platform commission. Final amount is calculated on our server.</p>
        </div>
        <Button className="w-full" size="lg" onClick={order} disabled={busy} data-testid="stripe-checkout-submit-button">{busy ? "Redirecting to Stripe…" : `Continue to checkout`}</Button>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" />Seller is credited only after you accept the delivery.</p>
      </div>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const api = useApi();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/services/${slug}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [slug, api]);
  const [fav, toggleFav] = useFavorite("service", d?.service.id, d?.is_favorite);
  if (err) return <div className="container-x py-24"><EmptyState title="Service not found" text={err} /></div>;
  if (!d) return <div className="container-x py-24"><Spinner /></div>;
  const s = d.service;
  return (
    <div className="container-x py-10">
      <div className="mb-6 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Link to="/services" className="hover:text-foreground">Services</Link> / <Link to={`/services?category=${s.category_slug}`} className="capitalize hover:text-foreground">{s.category_slug.replace(/-/g, " ")}</Link>
        {s.is_demo && <DemoBadge className="ml-2" />}{s.status !== "published" && <Tag>{s.status}</Tag>}
      </div>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-8">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="service-title">{s.title}</h1>
            <Button variant="outline" size="icon" onClick={toggleFav} data-testid="favorite-toggle-button" aria-label="Favorite"><Heart className={`h-4 w-4 ${fav ? "fill-rose-500 text-rose-500" : ""}`} /></Button>
          </div>
          <Stars avg={s.rating_avg} count={s.rating_count} />
          <Gallery images={s.images} title={s.title} />
          {s.video_url && <a href={s.video_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary">Watch video walkthrough →</a>}
          <div><h2 className="mb-3 text-lg font-semibold">About this service</h2><p className="whitespace-pre-line text-muted-foreground">{s.description}</p></div>
          <div className="flex flex-wrap gap-2">{[...s.skills, ...s.tags].filter((v, i, a) => a.indexOf(v) === i).map((t) => <Tag key={t}>{t}</Tag>)}</div>
          {s.faqs.length > 0 && <div><h2 className="mb-3 text-lg font-semibold">FAQ</h2><Accordion type="single" collapsible>{s.faqs.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`}><AccordionTrigger data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger><AccordionContent>{f.a}</AccordionContent></AccordionItem>))}</Accordion></div>}
          <div><h2 className="mb-4 text-lg font-semibold">Reviews</h2><Reviews reviews={d.reviews} /></div>
        </div>
        <div className="space-y-6 lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-6">
            {d.is_owner ? <div className="card-surface p-6"><p className="text-sm">This is your listing.</p><Button asChild className="mt-3 w-full" data-testid="edit-own-service"><Link to={`/dashboard/vendor/services/${s.id}`}>Edit service</Link></Button></div>
              : <PackagePanel s={s} commission={d.commission_pct} />}
            {d.vendor && <SellerBox vendor={d.vendor} />}
          </div>
        </div>
      </div>
      {d.related.length > 0 && <div className="mt-20"><h2 className="mb-6 text-xl font-bold">Related services</h2><div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{d.related.map((r) => <ServiceCard key={r.id} s={r} />)}</div></div>}
    </div>
  );
}

export function ProductDetail() {
  const { slug } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const { isSignedIn } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [coupon, setCoupon] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/products/${slug}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [slug, api]);
  const [fav, toggleFav] = useFavorite("product", d?.product.id, d?.is_favorite);
  if (err) return <div className="container-x py-24"><EmptyState title="Product not found" text={err} /></div>;
  if (!d) return <div className="container-x py-24"><Spinner /></div>;
  const p = d.product;
  const buy = async () => {
    if (!isSignedIn) return nav("/sign-in");
    setBusy(true);
    try { const { data } = await api.post("/orders/product", { product_id: p.id, coupon_code: coupon }); await startCheckout(api, data.order_id); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const download = async () => {
    try { await downloadBlob(api, `/products/${p.id}/download`, `${p.slug}.zip`); } catch (e) { toast.error("Download failed — " + (e?.response?.status === 403 ? "purchase required" : "no file available")); }
  };
  return (
    <div className="container-x py-10">
      <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground"><Link to="/products" className="hover:text-foreground">Digital products</Link> / <span className="capitalize">{p.product_type.replace("_", " ")}</span>{p.is_demo && <DemoBadge className="ml-2" />}</div>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-8">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="product-title">{p.title}</h1>
            <Button variant="outline" size="icon" onClick={toggleFav} data-testid="favorite-toggle-button" aria-label="Favorite"><Heart className={`h-4 w-4 ${fav ? "fill-rose-500 text-rose-500" : ""}`} /></Button>
          </div>
          <Gallery images={p.images} title={p.title} />
          <p className="whitespace-pre-line text-muted-foreground">{p.description}</p>
          <div className="flex flex-wrap gap-2">{p.tech_stack.map((t) => <Tag key={t}>{t}</Tag>)}</div>
          <div><h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><History className="h-5 w-5" />Changelog</h2>
            <div className="space-y-3">{p.changelog.map((c, i) => <div key={i} className="border-l-2 border-border pl-4"><p className="font-mono text-sm font-semibold">v{c.version} <span className="font-sans font-normal text-muted-foreground">{c.date}</span></p><p className="text-sm text-muted-foreground">{c.notes}</p></div>)}</div></div>
          <div><h2 className="mb-4 text-lg font-semibold">Reviews</h2><Reviews reviews={d.reviews} /></div>
        </div>
        <div className="space-y-6 lg:col-span-4">
          <div className="card-surface space-y-4 p-6 lg:sticky lg:top-24">
            <p className="text-3xl font-bold" data-testid="product-price">{money(p.price_cents)}</p>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><FileCode className="h-4 w-4 text-muted-foreground" />Version {p.version}</p>
              <p className="flex items-center gap-2 capitalize"><Scale className="h-4 w-4 text-muted-foreground" />{p.license} license</p>
              <p className="flex items-center gap-2"><Download className="h-4 w-4 text-muted-foreground" />{p.has_file ? `Download · ${(p.file_size / 1048576).toFixed(1)} MB` : "No file uploaded yet (demo)"}</p>
            </div>
            {p.preview_url && <Button asChild variant="outline" className="w-full"><a href={p.preview_url} target="_blank" rel="noreferrer" data-testid="product-preview-link">Live preview</a></Button>}
            {d.owned || d.is_owner ? (
              <Button className="w-full" size="lg" onClick={download} disabled={!p.has_file} data-testid="product-download-button"><Download className="mr-2 h-4 w-4" />Download</Button>
            ) : (
              <>
                <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
                <Button className="w-full" size="lg" onClick={buy} disabled={busy} data-testid="product-buy-button">{busy ? "Redirecting to Stripe…" : "Buy now"}</Button>
                <p className="text-xs text-muted-foreground">Instant access after Stripe confirms payment. Refunds available within 14 days on request.</p>
              </>
            )}
          </div>
          {d.vendor && <SellerBox vendor={d.vendor} />}
        </div>
      </div>
    </div>
  );
}
